import { DIRECTION_ANGLES, LANDMARK_NAMES, LANDMARK_TOKENS, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
function pickN(arr, n) {
    const pool = [...arr];
    const out = [];
    while (out.length < n && pool.length > 0) {
        const i = Math.floor(Math.random() * pool.length);
        out.push(pool.splice(i, 1)[0]);
    }
    return out;
}
/** Calcule l'angle direction (en degrés horloge) entre 2 landmarks et le snap au 45° le plus proche. */
function snapDirection(from, to) {
    // Système de coordonnées : col = x (E+), row = y (S+). Nord = -y.
    const dx = to.col - from.col;
    const dy = to.row - from.row;
    // Angle classique math : atan2(dy, dx) avec dy positif vers le bas.
    // Convert vers angle horloge (0=N, 90=E) :
    // - Nord (-y) → 0
    // - Est (+x) → 90
    // - Sud (+y) → 180
    // - Ouest (-x) → 270
    // angle = atan2(dx, -dy) en degrés
    let angleDeg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    if (angleDeg < 0)
        angleDeg += 360;
    // Snap au multiple de 45 le plus proche.
    const snapped = Math.round(angleDeg / 45) * 45;
    return (snapped % 360);
}
export function buildStimulus(trialIdx, nLandmarks, level) {
    const variant = variantForLevel(level);
    const N = variant.gridSize;
    const safeN = Math.max(2, Math.min(Math.round(nLandmarks), Math.min(LANDMARK_TOKENS.length, N * N)));
    const tokens = pickN(LANDMARK_TOKENS, safeN);
    const names = pickN(LANDMARK_NAMES, safeN);
    // Place chaque landmark à une position unique.
    const usedKeys = new Set();
    const landmarks = [];
    let attempts = 0;
    while (landmarks.length < safeN && attempts < 500) {
        attempts += 1;
        const row = Math.floor(Math.random() * N);
        const col = Math.floor(Math.random() * N);
        const key = `${row}-${col}`;
        if (usedKeys.has(key))
            continue;
        usedKeys.add(key);
        const idx = landmarks.length;
        landmarks.push({
            id: `${trialIdx}-${idx}`,
            token: tokens[idx] ?? LANDMARK_TOKENS[0],
            name: names[idx] ?? 'Landmark',
            row,
            col,
        });
    }
    // Choisit deux landmarks distincts assez éloignés (≥ 2 cellules manhattan).
    let fromIdx = 0;
    let toIdx = 1;
    for (let tries = 0; tries < 50; tries += 1) {
        const a = Math.floor(Math.random() * landmarks.length);
        let b = Math.floor(Math.random() * landmarks.length);
        if (b === a)
            b = (a + 1) % landmarks.length;
        const La = landmarks[a];
        const Lb = landmarks[b];
        if (Math.abs(La.row - Lb.row) + Math.abs(La.col - Lb.col) >= 2) {
            fromIdx = a;
            toIdx = b;
            break;
        }
        fromIdx = a;
        toIdx = b;
    }
    const from = landmarks[fromIdx];
    const to = landmarks[toIdx];
    const expectedDirection = snapDirection(from, to);
    return {
        trialIdx,
        gridSize: N,
        landmarks,
        explorationMs: variant.explorationMs,
        fromId: from.id,
        toId: to.id,
        expectedDirection,
    };
}
export function createMentalMapAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    return {
        mount() { },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'pre_exploration', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'exploration' });
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'pre_test' });
                        timers.push(setTimeout(() => {
                            update({ subPhase: 'awaiting_response' });
                            trialStart = Date.now();
                        }, timing.preTestMs));
                    }, stim.explorationMs));
                }, timing.preExplorationMs));
            });
        },
        async showFeedback(correct) {
            update({ subPhase: 'feedback', feedback: correct ? 'correct' : 'incorrect' });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() { },
        resume() { },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleDirectionPick(direction) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            // Tolérance ±45° (1 cran d'erreur OK pour simuler "un peu plus à l'est").
            const diff = Math.abs(direction - stim.expectedDirection);
            const wrapDiff = Math.min(diff, 360 - diff);
            const correct = wrapDiff <= 45;
            const rt = Date.now() - trialStart;
            const resp = { pick: direction, correct, reactionTimeMs: rt };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
export { snapDirection };
export const ALL_DIRECTIONS = DIRECTION_ANGLES;
//# sourceMappingURL=adapter.js.map